/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import _ from 'underscore';
import React from 'react';

import i18n from '../../../../i18n/AdHocBundle.properties';
import layoutRequirements from '../enum/layoutRequirements';

const ALL_ITEMS_TAB_VALUE = 'all';

export default class BodyComponent extends React.Component {

    constructor(props) {
        super(props);

        this.listPosition = 0;
        this.state = {
            hoveredType: null
        };

        this._onListScroll = this._onListScroll.bind(this);
    }

    getState() {
        return {
            scrollTop: this.getScrollPosition()
        };
    }

    getScrollPosition () {
        return this.refs.listOfVisualizations.scrollTop;
    }

    applyState(state) {
        // if we call next method without timeout the next would happen:
        // for some reasons react changes scrollTop of the element to 0
        // and calls the scrollEvent after this, which doesn't make any sense.
        // To avoid it and actually retain control over the scroll, we'd set the scroll
        // after some time
        setTimeout(() => {
            this.setScrollPosition(state.scrollTop);
        }, 100);
    }

    setScrollPosition (position) {
        this.listPosition = position;
        this.refs.listOfVisualizations.scrollTop = position;
    }

    _isTypeDisabled (typeName) {
        return _.contains(this.props.disabledTypes, typeName);
    }

    _canBeSelectedByUser (typeName) {
        return !this._isTypeDisabled(typeName);
    }

    _getRulesForLayoutRequirements() {

        const visualizationType = this.state.hoveredType || this.props.selectedType;

        if (!visualizationType) {
            return null;
        }

        const details = layoutRequirements[visualizationType];

        return details.rules.map((rule, ruleName) => {

            let first = rule.first;
            let second = (rule.second && rule.second.msg) ? rule.second.msg : rule.second;
            let third = rule.third;
            let fourth = (rule.fourth && rule.fourth.msg) ? rule.fourth.msg : rule.fourth;
            let fifth = rule.fifth ? rule.fifth : '';
            let twoSpan = (rule.type && rule.type === 'twospan');

            let colorClass = 'jr-uColorMeasure';
            if (rule.second && rule.second.type && rule.second.type === 'field') {
                colorClass = 'jr-uColorField';
            }

            return (
                <li key={ ruleName } className="jr-mList-item jr">
                    <span className="jr-mList-item-text jr">
                        { first }&nbsp;
                        <span className={`jr jr-uColorField jr-uSemibold ${colorClass}`}>
                            { second }&nbsp;
                        </span>
                        { third }&nbsp;
                        { twoSpan && (
                            <div>
                                <span className={`jr jr-uColorMeasure jr-uSemibold ${colorClass}`}>
                                    { fourth }&nbsp;
                                </span>
                                { fifth }
                            </div>
                        )}
                    </span>
                </li>
            );
        });
    }

    _getLayoutRequirementsSection() {
        if (!this.props.inDesignMode) {
            return null;
        }

        let visualizationName = '';
        let iconClassName = 'jr-isHidden';
        let currentSelectionClasses = 'jr-isHidden';

        const visualizationType = this.state.hoveredType || this.props.selectedType;
        const details = layoutRequirements[visualizationType];

        if (visualizationType) {
            const typeDescription = this.props.typeManager.getTypeByName(visualizationType);
            visualizationName = i18n[typeDescription.bundleName];
            iconClassName = details.iconClassName;
        }

        if (visualizationType && visualizationType === this.props.selectedType) {
            currentSelectionClasses = '';
        }

        return (
            <div className="jr-mVisualchooser-rules jr">
                <div className="jr-mVisualchooser-rule  jr">
                    <div className={ `jr jr-mVisualchooser-rule-icon ${iconClassName}` } />
                    <div className="jr-mVisualchooser-rule-text jr">
                        <ul className="jr-mList jr-mListSmall jr">
                            <li className="jr-mList-item jr">
                                <span className="jr-mList-item-title jr">
                                    <span className={ `jr jr-mVisualchooser-rule-text-label ${currentSelectionClasses}` }>
                                        { i18n['visualization.chooser.currentSelection'] }:&nbsp;
                                    </span>
                                    <span>{ visualizationName }</span>
                                </span>
                            </li>
                            { this._getRulesForLayoutRequirements() }
                        </ul>
                    </div>
                </div>
            </div>
        )
    }

    render() {

        let typesToRender = this.props.typeManager.getAllTypes();
        if (this.props.selectedGroup !== ALL_ITEMS_TAB_VALUE) {
            typesToRender = this.props.typeManager.getAllTypesInGroup(this.props.selectedGroup);
        }

        const itemStyles = {
            listStyle: 'none'
        };

        const itemElements = typesToRender.map((type) => {
            const isSelectedClass = type.name === this.props.selectedType ? 'jr-isSelected' : '';
            const isDisabledClass = this._canBeSelectedByUser(type.name)  ? '' : 'jr-isDisabled';
            return (
                <li
                    key={ type.name }
                    className={`jr jr-mVisualchooser-menu-item ${isSelectedClass} ${isDisabledClass}`}
                    style={ itemStyles }
                >
                    <div className="jr jr-mVisualchooser-menu-item-wrapper"
                        onClick={ this._onItemClick.bind(this, type) }
                        onMouseOver={ this._onItemMouseOver.bind(this, type) }
                        onMouseOut={ this._onItemMouseOut.bind(this) }
                    >
                        <div className={`jr jr-mVisualchooser-menu-item-icon jr-mVisualchooser-menu-item-icon${type.cssClass}`} />
                        <span className="jr jr-mVisualchooser-menu-item-label">{ i18n[type.bundleName] }</span>
                    </div>
                </li>
            )
        });

        return (
            <div className="jr-mVisualchooser-body jr">
                <ul ref="listOfVisualizations" className="jr-mVisualchooser-menu jr" onScroll={ this._onListScroll }>
                    { itemElements }
                </ul>

                { this._getLayoutRequirementsSection() }
            </div>
        );
    }

    componentDidUpdate() {
        this.refs.listOfVisualizations.scrollTop = this.listPosition;
    }

    _onListScroll() {
        this.listPosition = this.refs.listOfVisualizations.scrollTop;
    }

    _onItemClick(type) {
        if (!this._canBeSelectedByUser(type.name)) {
            return;
        }
        if (this.props.selectedType === type.name) {
            return;
        }

        this.props.onVisualizationChange({
            type: type.name,
            legacyAdhocType: type.legacyAdhoc
        });
    }

    _onItemMouseOver(type) {
        this.setState({
            hoveredType: type.name
        });
    }

    _onItemMouseOut(event) {
        this.setState({
            hoveredType: null
        });
    }
}
